/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * AssignmentUpdateMainComponent
 *
 * @file AssignmentUpdateMainComponent.jsx
 * @author Ujjwal Sana
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import AssignmentForm from './assignments/AssignmentForm';
import { ASSIGNMENT_FORM_MODE } from './assignments/assignmentFormActions';
import { DEFAULT_ASSIGNMENT_FORM_VALUES } from '../common/syllabusUploadContract';

const AssignmentUpdateMainComponent = ({
  syllabusProps,
  assignmentFormMode = ASSIGNMENT_FORM_MODE.CREATE,
  initialValues = DEFAULT_ASSIGNMENT_FORM_VALUES,
  formTextInfo = {},
  actions = [],
  editingIndex = null,
  isLoading = false,
  restrictAlreadyAssigned,
  currentAssignedList,
  addAssignmentCount,
  assignmentConfig,
  timeZoneOptions,
  onTimeZoneChange
}) => {
  if (isLoading) {
    return null;
  }

  return (
    <AssignmentForm
      mode={assignmentFormMode}
      initialValues={initialValues}
      textInfo={formTextInfo}
      tocData={syllabusProps.tocData}
      chapterMcqMapping={syllabusProps.chapterMcqMapping}
      restrictAssigned={restrictAlreadyAssigned}
      existingAssignments={currentAssignedList}
      currentAssignmentIndex={editingIndex}
      formKey={addAssignmentCount || 0}
      config={assignmentConfig || syllabusProps.assignmentConfig}
      actions={actions}
      onScheduleMenuAction={syllabusProps.onScheduleMenuAction}
      timeZoneOptions={timeZoneOptions ?? syllabusProps.timeZoneOptions ?? []}
      onTimeZoneChange={onTimeZoneChange ?? syllabusProps.onTimeZoneChange}
    />
  );
};

AssignmentUpdateMainComponent.propTypes = {
  syllabusProps: PropTypes.object.isRequired,
  assignmentFormMode: PropTypes.oneOf([ASSIGNMENT_FORM_MODE.CREATE, ASSIGNMENT_FORM_MODE.EDIT]),
  initialValues: PropTypes.object,
  formTextInfo: PropTypes.object,
  actions: PropTypes.arrayOf(PropTypes.object),
  editingIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  restrictAlreadyAssigned: PropTypes.bool,
  currentAssignedList: PropTypes.array,
  addAssignmentCount: PropTypes.number,
  isLoading: PropTypes.bool,
  assignmentConfig: PropTypes.object,
  timeZoneOptions: PropTypes.array,
  onTimeZoneChange: PropTypes.func
};

AssignmentUpdateMainComponent.defaultProps = {
  assignmentFormMode: ASSIGNMENT_FORM_MODE.CREATE,
  initialValues: DEFAULT_ASSIGNMENT_FORM_VALUES,
  formTextInfo: {},
  actions: [],
  editingIndex: null,
  restrictAlreadyAssigned: true,
  currentAssignedList: null,
  addAssignmentCount: 0,
  isLoading: false,
  assignmentConfig: null,
  timeZoneOptions: null,
  onTimeZoneChange: null
};

export default AssignmentUpdateMainComponent;
